import { Menu, Pill, Tooltip, Typography } from '@recruit-robin/robin-theme/web-components';
import { Database_01, Edit_05, Indeed, Star_06, Trash_01 } from '@recruit-robin/robin-theme/web-icons';
import { motion } from 'motion/react';
import { useContext, useMemo, useState } from 'react';

import type { VacancyItemProps } from './VacancyListItem.types';

import { SerializedStyles } from '@emotion/react';
import { AnimatePresence } from 'motion/react';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { IndeedTooltip } from '../IndeedTooltip/IndeedTooltip';
import { useStyles } from './VacancyListItem.styles';

export const VacancyListItem = ({
  disableTooltip,
  vacancy,
  showEnrichedVacancyButton = true,
  isSelected,
  onSelect,
  onDelete,
  onEdit,
}: VacancyItemProps) => {
  const classes = useStyles();
  const { dictionary } = useContext(LocalizationContext);

  const [isHovered, setIsHovered] = useState(false);

  const tooltip = useMemo(() => {
    const indeedJob = vacancy.type === 'INDEED' && vacancy.originalIndeedJob;
    if (!indeedJob) return null;

    return <IndeedTooltip indeedJob={indeedJob} />;
  }, [vacancy]);

  return (
    <Tooltip
      customCss={classes.tooltip}
      label={tooltip}
      zIndex={1000}
      placement="left"
      disabled={!tooltip || disableTooltip}>
      <Menu.Item.Root
        customCss={classes.menuItem}
        onClick={() => onSelect(vacancy)}
        selected={isSelected}
        variant="subtle"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        <div css={classes.iconContainer}>
          {vacancy.is_improved ? (
            <Star_06 size={16} />
          ) : vacancy.job_indeed_id ? (
            <Indeed size={16} />
          ) : (
            <Database_01 size={16} />
          )}
        </div>

        <Typography variant={(v) => v.supporting.helper} ellipsis>
          {vacancy.name}
        </Typography>

        <div css={classes.menuItemActions}>
          <AnimatePresence mode="popLayout">
            <motion.div
              key={isHovered ? 'hovered' : 'not-hovered'}
              css={classes.enrichedVacancyStatus}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.17, ease: [0.22, 0.9, 0.53, 1] }}>
              {vacancy.is_improved && !isHovered && (
                <Pill
                  label={
                    !vacancy.quality_percent || vacancy.quality_percent <= 33
                      ? dictionary.lowQuality
                      : vacancy.quality_percent <= 66
                        ? dictionary.mediumQuality
                        : dictionary.highQuality
                  }
                  style="icon-removable"
                  type={getQualityPillType(vacancy.quality_percent)}
                  size="small"
                  onClick={() => undefined}
                  padding="text"
                />
              )}

              {isHovered && (
                <>
                  {onDelete && vacancy.is_improved && (
                    <SimpleButton
                      key="remove-enriched-version"
                      label={dictionary.removeEnrichedVersion}
                      onClick={() => onDelete(vacancy)}
                      icon={<Trash_01 size={16} />}
                      customCss={classes.iconActionTrash}
                    />
                  )}

                  {onEdit && showEnrichedVacancyButton && (
                    <SimpleButton
                      key="edit-vacancy"
                      label={vacancy.is_improved ? dictionary.editVacancy : dictionary.enrichVacancy}
                      onClick={() => onEdit(vacancy)}
                      icon={<Edit_05 size={16} />}
                      customCss={classes.iconActionEdit}
                    />
                  )}
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </Menu.Item.Root>
    </Tooltip>
  );
};

const SimpleButton = ({
  icon,
  onClick,
  label,
  customCss,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  label: string;
  customCss?: SerializedStyles;
}) => {
  const classes = useStyles();

  return (
    <Tooltip label={label} zIndex={100} placement="bottom-end" offset={10}>
      <div
        css={[classes.iconAction, customCss]}
        onClick={(e) => {
          e.stopPropagation();

          onClick();
        }}>
        {icon}
      </div>
    </Tooltip>
  );
};

const getQualityPillType = (qualityPercent: number | null | undefined): 'error' | 'warning' | 'success-light' => {
  if (!qualityPercent || qualityPercent < 33) return 'error';
  if (qualityPercent < 66) return 'warning';
  return 'success-light';
};
